/**
 * Main Customer View - Choice between QR Code and SMS
 *
 * This component provides the initial choice for customers:
 * 1. QR Code scanning (existing functionality)
 * 2. SMS-based queue registration (new functionality)
 */
import React from "react";
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Link as MuiLink,
    Card,
    CardContent,
    CardActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FMBlogo from "../../../assests/FMBlogo.png";
import ScrollButtons from "../../../components/ScrollButtons";

const CustomerChoicePage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const handleQrCode = () => {
        navigate("/home/qr-display");
    };

    const handleSms = () => {
        navigate("/home/sms-customer-view");
    };

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
                sx={{ mt: { xs: 6, sm: 10 } }}
            >
                <Box mb={2}>
                    <img
                        src={FMBlogo}
                        alt="FMB Logo"
                        style={{
                            width: isMobile ? 180 : 300,
                            maxWidth: "100%",
                        }}
                    />
                </Box>

                <Typography
                    variant="h4"
                    color="primary"
                    textAlign="center"
                    mb={2}
                    sx={{ fontWeight: "bold" }}
                >
                    Velkommen til Køsystemet
                </Typography>

                <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    textAlign="center"
                    mb={4}
                    sx={{ maxWidth: 500 }}
                >
                    Velg hvordan du vil registrere deg i køen
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        gap: 3,
                        width: "100%",
                        maxWidth: 600,
                        justifyContent: "center",
                    }}
                >
                    {/* QR Code Option */}
                    <Card
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <CardContent>
                            <Box textAlign="center" mb={2}>
                                <Typography
                                    variant="h2"
                                    sx={{ fontSize: "3rem" }}
                                >
                                    📱
                                </Typography>
                            </Box>
                            <Typography
                                variant="h6"
                                color="primary"
                                textAlign="center"
                                mb={1}
                                sx={{ fontWeight: "bold" }}
                            >
                                QR-kode
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                            >
                                Skann QR-koden for å registrere deg i køen raskt
                                og enkelt
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "center", pb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleQrCode}
                                fullWidth
                                sx={{ mx: 2 }}
                            >
                                Bruk QR-kode
                            </Button>
                        </CardActions>
                    </Card>

                    {/* SMS Option */}
                    <Card
                        sx={{
                            flex: 1,
                            minHeight: 200,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                        }}
                    >
                        <CardContent>
                            <Box textAlign="center" mb={2}>
                                <Typography
                                    variant="h2"
                                    sx={{ fontSize: "3rem" }}
                                >
                                    💬
                                </Typography>
                            </Box>
                            <Typography
                                variant="h6"
                                color="primary"
                                textAlign="center"
                                mb={1}
                                sx={{ fontWeight: "bold" }}
                            >
                                SMS
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                textAlign="center"
                            >
                                Skriv inn telefonnummeret ditt og få kønummer
                                via SMS
                            </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "center", pb: 2 }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleSms}
                                fullWidth
                                sx={{ mx: 2 }}
                            >
                                Bruk SMS
                            </Button>
                        </CardActions>
                    </Card>
                </Box>

                <Box mt={4} textAlign="center">
                    <Typography variant="body2">
                        <MuiLink href="#" underline="hover">
                            easyQue
                        </MuiLink>{" "}
                        (c) by Digifront
                    </Typography>
                    <Typography variant="body2" mt={1}>
                        <MuiLink
                            href="mailto:sales@digifront.biz"
                            underline="hover"
                        >
                            sales@digifront.biz
                        </MuiLink>
                    </Typography>
                </Box>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default CustomerChoicePage;
